import router from "../router";
import services from "../services";
import Utils from "../Utils";

const strict = true;
const getDefaultState = () => {
  return {
    loginBasicAuth: "",
    userInfo: {},
    isLoggedIn: false,
    usernameAvailability: false,
    firstLoginToken: null,
    useLoadingDialog: true,
    incomingForSla: null
  };
};

const state = getDefaultState();

const getters = {
  isUserLoggedIn: state => userId => userId == state.userInfo.id,
  usernameAvailability: state => state.usernameAvailability,
  loginBasicAuth: state => state.loginBasicAuth,
  userInfo: state => state.userInfo,
  userPermissionList: state => state.userInfo.permissionList,
  userFullName: state => state.userInfo.name + " " + state.userInfo.surname,
  firstLoginToken: state => {
    return state.firstLoginToken;
  },
  useLoadingDialog: state => state.useLoadingDialog,
  isLoggedIn: state => state.isLoggedIn,
  userHasRequiredAccess: state => (permName, requiredAccessRights) => {
    //  check if user has access to this page
    let userPermissionList = state.userInfo.permissionList || [];
    let userPermissionPage = userPermissionList.find(
      permission => permission.portalArea == permName
    );
    if (!userPermissionPage) {
      // return true if the page does not have permissions
      return true;
    }
    if (userPermissionPage.perm === "NOREAD") {
      // return false if the user has a "NOREAD" permission
      return false;
    } else {
      if (
        requiredAccessRights === "READWRITE" &&
        userPermissionPage.perm === "READ"
      ) {
        // return false if the user has a "READ" permission but a "READWRITE" permission is required
        // (only used to hide in page "write" components like buttons etc... and not to block the enitre page)
        return false;
      }
    }
    if (permName === "CENTRONOTIFICHE") {
      // if the user has the permission for the "CENTRONOTIFICHE" we need to check if the
      // same user has also a valid permission on "RENDICONTAZIONE" or on "GESTIONERICHIESTE"
      // because the "type" notification filter is populated based on these two permissions
      let userPermissionPageForBill = userPermissionList.find(
        permission => permission.portalArea === "RENDICONTAZIONE"
      );
      let userPermissionPageForGestRich = userPermissionList.find(
        permission => permission.portalArea === "GESTIONERICHIESTE"
      );
      if (!userPermissionPageForBill && !userPermissionPageForGestRich) {
        return false;
      } else {
        let validBillPerm = userPermissionPageForBill
          ? userPermissionPageForBill.perm != "NOREAD"
          : false;
        let validGestRichPerm = userPermissionPageForGestRich
          ? userPermissionPageForGestRich.perm != "NOREAD"
          : false;
        return validBillPerm || validGestRichPerm;
      }
    }
    return true;
  },
  filteredHfOffices() {
    return officesList => {
      return officesList.filter(office => {
        let userRoles = office.role;
        return userRoles.some(role => {
          return (
            role === "OPERATIONMANAGER" ||
            role === "FINANCIALMANAGER" ||
            role === "OPERATOR"
          );
        });
      });
    };
  },
  getIncomingForSla: state => state.incomingForSla
};

const mutations = {
  RESET(state) {
    // acquire initial state
    const s = getDefaultState();
    Object.keys(s).forEach(key => {
      state[key] = s[key];
    });
  },
  SET_USER_AUTH_TOKEN(state, { username, password }) {
    state.loginBasicAuth = "Basic " + btoa(username + ":" + password);
  },
  SET_FIRST_LOGIN_TOKEN(state, token) {
    state.firstLoginToken = encodeURIComponent(token.split("=")[1]);
  },
  SET_USER_INFO(state, userInfo) {
    state.userInfo = userInfo;
  },
  SET_USER_LOGGED_IN_STATUS(state, status) {
    state.isLoggedIn = status;
  },
  SET_USER_PERM(state, permList) {
    if (!permList) return;
    state.userInfo.permissionList = permList;
  },
  SET_USER_AVAILABILITY_STATE(state, availability) {
    state.usernameAvailability = availability;
  },
  SET_USE_LOADING_DIALOG(state, value) {
    state.useLoadingDialog = value;
  },
  setIncomingForSla(state, obj) {
    state.incomingForSla = obj;
  }
};

const actions = {
  // LOGIN
  login({ commit, getters }, { username, password }) {
    commit("SET_USER_INFO", {});
    commit("SET_USER_AUTH_TOKEN", {
      username,
      password
    });
    return services.loginService
      .getUserInfo()
      .then(userInfo => {
        commit("SET_USER_LOGGED_IN_STATUS", true);
        commit("SET_USER_INFO", userInfo);
        commit("session/actionMoment", null, {
          root: true
        });
        return services.hfManagementService.fetchHF(userInfo.hfId);
      })
      .then(hfInfoContainer => {
        commit("hfManagement/SET_HF", hfInfoContainer.hf, {
          root: true
        });
        // FILTER OFFICES BASED ON USER'S ROLE ON THEM
        let filteredOffices = getters["filteredHfOffices"](
          hfInfoContainer.listHfOfficeInfo
        );
        commit("hfManagement/SET_HF_OFFICES", filteredOffices, {
          root: true
        });
        let incomingForSla = getters["getIncomingForSla"] ? true : false;
        let incomingOfficeId = null;
        let incomingSlaType = null;
        let incomingRedirectType = null;
        let nextState;
        let numberOfOffices = 0;
        if (incomingForSla) {
          numberOfOffices = 1;
          incomingOfficeId = getters["getIncomingForSla"].officeId;
          incomingSlaType = getters["getIncomingForSla"].slaType;
          incomingRedirectType = getters["getIncomingForSla"].redirectType;
        } else {
          numberOfOffices = filteredOffices.length;
        }
        if (numberOfOffices === 0) {
          let errorDialogDetails = {
            type: Utils.errorTypes.CUSTOM,
            title: "homepage.login.errors.no-offices.title",
            msg: "homepage.login.errors.no-offices.msg",
            actionBtn: "homepage.login.errors.no-offices.btn"
          };
          commit("error/SET_ERROR", errorDialogDetails, {
            root: true
          });
        } else if (numberOfOffices === 1) {
          // check on permission list
          let office = incomingForSla
            ? filteredOffices.find(off => off.id === incomingOfficeId)
            : filteredOffices[0];
          let hfOfficePermissions = office ? office.permission : [];
          if (!office || hfOfficePermissions.length === 0) {
            let errorDialogDetails = {
              type: Utils.errorTypes.CUSTOM,
              title: "homepage.login.errors.no-premissions.title",
              msg: "homepage.login.errors.no-premissions.msg",
              actionBtn: "homepage.login.errors.no-premissions.btn"
            };
            commit("error/SET_ERROR", errorDialogDetails, {
              root: true
            });
          } else {
            let officeId = office.id;
            commit("hfManagement/SET_OFFICE_ID", officeId, {
              root: true
            });
            commit(
              "hfManagement/SET_SANIMPRESA_OFFICE",
              office.sanimpresa || false,
              {
                root: true
              }
            );
            // INIT USER ROLES AND PERMISSIONS FOR THE SELECTED HF OFFICE
            commit("hfManagement/SET_USER_ROLES", office.role, {
              root: true
            });
            commit("SET_USER_PERM", hfOfficePermissions);
            if (incomingForSla) {
              if (incomingRedirectType === "bills") {
                let activeItems = ["SENTBACK"];
                commit("bills/SET_MULTI_SELECT_ACTIVE_LIST", activeItems, {
                  root: true
                });
                commit("bills/SET_ORDER", "A-Z", {
                  root: true
                });
                commit("bills/SET_ORDER_FIELD", "slaDueDate", {
                  root: true
                });
                nextState = "_bills";
              } else {
                if (incomingSlaType === "APPOINTMENT_REQUEST") {
                  commit("appointments/setType", "request", {
                    root: true
                  });
                  let activeItems = ["PENDING"];
                  commit(
                    "appointments/SET_MULTI_SELECT_ACTIVE_LIST",
                    activeItems,
                    {
                      root: true
                    }
                  );
                  let order = {
                    descending: [false],
                    sortBy: ["slaDueDate"]
                  };
                  commit("appointments/SET_ORDER", order, {
                    root: true
                  });
                  nextState = "_request-appointments";
                } else {
                  commit("appointments/setType", "booking", {
                    root: true
                  });
                  if (incomingSlaType === "APPOINTMENT_MODIFY_DATE") {
                    let activeItems = ["MEMBERCHANGE"];
                    commit(
                      "appointments/SET_MULTI_SELECT_ACTIVE_LIST",
                      activeItems,
                      {
                        root: true
                      }
                    );
                  }
                  if (incomingSlaType === "APPOINTMENT_CANCELLATION") {
                    let activeItems = [
                      "APPOINTMENTCANCELLEDBYMEMBER",
                      "APPOINTMENTCANCELLEDBYNEST"
                    ];
                    commit(
                      "appointments/SET_MULTI_SELECT_ACTIVE_LIST",
                      activeItems,
                      {
                        root: true
                      }
                    );
                  }
                  let order = {
                    descending: [false],
                    sortBy: ["slaDueDate"]
                  };
                  commit("appointments/SET_ORDER", order, {
                    root: true
                  });
                  nextState = "_booking-appointments";
                }
              }
            } else {
              nextState = "home";
            }
          }
        } else {
          nextState = "hfSelection";
        }
        router.push({
          name: nextState
        });
      })
      .catch(error => {
        commit("SET_USER_LOGGED_IN_STATUS", false);
        let errorDialogDetails;
        if (error.reason.response?.status == 401) {
          let e = error.reason.response.headers["x-onair-exception-type"];
          if (e == "nest.common.UserNotAssociatedToAnyOfficeException") {
            errorDialogDetails = {
              type: Utils.errorTypes.CUSTOM,
              title: "homepage.login.errors.no-offices.title",
              msg: "homepage.login.errors.no-offices.msg",
              actionBtn: "homepage.login.errors.no-offices.btn"
            };
          }
        } else {
          errorDialogDetails = {
            ...error
          };
          errorDialogDetails.type = Utils.errorTypes.STATUS;
          errorDialogDetails.dataName = "homepage.login";
        }
        commit("error/SET_ERROR", errorDialogDetails, {
          root: true
        });
      });
  },
  // FIRST LOGIN
  firstLogin({ commit, dispatch, state }, { username, password }) {
    commit("SET_USER_AUTH_TOKEN", {
      username,
      password
    });
    let token = state.firstLoginToken;
    return services.loginService
      .activateUser(token, username, password)
      .then(() => {
        let success = {
          title: "homepage.firstLogin.success.title",
          message: "homepage.firstLogin.success.message",
          closeButtonLabel: "homepage.firstLogin.success.closeButton",
          closeButtonClb: () => {
            dispatch("logout", null, { root: true });
          }
        };
        commit("success/openSuccessDialog", success, {
          root: true
        });
      })
      .catch(error => {
        commit("SET_USER_LOGGED_IN_STATUS", false);
        let errorDialogDetails = {
          ...error
        };
        if (error.type == Utils.errorTypes.STATUS) {
          errorDialogDetails.dataName = "homepage.firstLogin";
        }
        commit("error/SET_ERROR", errorDialogDetails, {
          root: true
        });
      })
      .finally(() => {});
  },
  // CHECK USERNAME
  checkUsername({ commit }, username) {
    commit("SET_USE_LOADING_DIALOG", false);
    services.loginService
      .checkUsername(username)
      .then(usernameAvailability => {
        commit("SET_USER_AVAILABILITY_STATE", usernameAvailability);
        commit("SET_USE_LOADING_DIALOG", true);
      })
      .catch(() => {
        commit("SET_USER_LOGGED_IN_STATUS", false);
        commit("SET_USE_LOADING_DIALOG", true);
      })
      .finally(() => {});
  }
};

export default {
  namespaced: true,
  strict,
  state,
  getters,
  mutations,
  actions
};
